import { useEffect, useState } from "react";
import { Mail } from "lucide-react";
import { errorMessage } from "../../../api/client";
import { emailApi, type EmailStatus, type EmailTestResult } from "../../../api/email";
import { PageHeader } from "../../../components/ComingSoon";
import { Button, ErrorText, Field, TextInput } from "../../../components/ui";
import { Card, CheckIcon, CodeField } from "./twilio/parts";

const SOURCE = { own: "Your Resend key (RESEND_API_KEY)", managed: "Emergent-managed email", none: "Not configured" };

function Row({ ok, label, testId }: { ok: boolean; label: string; testId: string }) {
  return <li className="flex items-center gap-2 text-sm text-zinc-300" data-testid={testId}><CheckIcon ok={ok} required />{label}</li>;
}

function Result({ r }: { r: EmailTestResult }) {
  return (
    <div data-testid="email-test-result" className="mt-4 rounded-xl border border-white/10 p-3 text-sm">
      <p className={r.ok ? "font-semibold text-emerald-300" : "font-semibold text-amber-200"} data-testid="email-test-outcome">
        {r.ok ? `Sent to ${r.to}` : `Not sent to ${r.to}`}{r.status_code ? ` · HTTP ${r.status_code}` : ""}{r.error_code ? ` · ${r.error_code}` : ""}
      </p>
      <p className="mt-1 text-zinc-400" data-testid="email-test-message">{r.message}</p>
      {r.id && <p className="mt-1 font-mono text-xs text-zinc-500">Resend id {r.id}</p>}
    </div>
  );
}

export default function EmailSettings() {
  const [st, setSt] = useState<EmailStatus | null>(null);
  const [to, setTo] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [res, setRes] = useState<EmailTestResult | null>(null);
  useEffect(() => { emailApi.status().then(setSt).catch((e) => setError(errorMessage(e))); }, []);
  const send = async () => {
    setBusy(true); setError(null);
    try { setRes(await emailApi.sendTest(to.trim(), code)); setCode(""); } catch (e) { setError(errorMessage(e)); } finally { setBusy(false); }
  };
  return (
    <section data-testid="email-settings-page" className="animate-rise space-y-6">
      <PageHeader title="Email" />
      {st && (
        <Card title="Email sending" icon={<Mail className="h-5 w-5 text-signal" />} testId="email-status-card">
          <dl className="grid gap-2 text-sm sm:grid-cols-[10rem_1fr]">
            <dt className="text-zinc-500">Active source</dt><dd className="text-white" data-testid="email-source">{SOURCE[st.source ?? "none"]}</dd>
            <dt className="text-zinc-500">From address</dt><dd className="break-all font-mono text-zinc-200" data-testid="email-from">{st.from}</dd>
          </dl>
          <ul className="mt-4 space-y-1.5">
            <Row ok={st.own_key_set} label="RESEND_API_KEY set in Secrets" testId="email-check-key" />
            <Row ok={st.email_from_set} label="EMAIL_FROM set in Secrets" testId="email-check-from" />
            <Row ok={st.domain_ok} label={`Sends from your own domain (${st.from_domain || "—"})`} testId="email-check-domain" />
          </ul>
          <p className="mt-3 text-xs text-zinc-500">Your own key wins over the managed key when both RESEND_API_KEY and EMAIL_FROM are set. Change them in Secrets, then republish.</p>
        </Card>
      )}
      <Card title="Send test email" icon={<Mail className="h-5 w-5 text-signal" />} testId="email-test-card">
        <p className="text-sm text-zinc-400">Sends a short test message through the active source. Limit 5 every 10 minutes.</p>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <Field label="Send to (blank = your own address)" htmlFor="email-test-to"><TextInput id="email-test-to" type="email" value={to} onChange={(e) => setTo(e.target.value)} data-testid="email-test-to" /></Field>
          <CodeField id="email-test-code" value={code} onChange={setCode} />
        </div>
        <ErrorText message={error} testId="email-test-error" />
        <Button className="mt-4" onClick={send} loading={busy} disabled={code.length < 6} data-testid="email-send-test">Send test email</Button>
        {res && <Result r={res} />}
      </Card>
    </section>
  );
}
